import type {
	ColorsFilter,
	Filter,
	FiltersData,
	GenericFilter,
	GenericUserFilter,
	OrderFilter,
	PriceFilter,
	SizesFilter,
	UserFilter,
} from 'plp/types/Filters'

import { ALL, NEW_COLLECTION, ON_SALE } from './constants'

export function getIdValue(str: string) {
	return str.split('_').at(1) ?? str
}

export function getOptionId(
	type: string,
	choice: string | number,
	id?: string
) {
	const hasId = id !== undefined ? `-${id}` : ''
	return `${type}${hasId}_${choice.toString()}`
}

function genericMapper(filter: GenericFilter): GenericUserFilter {
	const { type, id, choices, multipleChoice, label } = filter
	let options = choices.map((choice) => ({
		...choice,
		id: getOptionId(type, choice.id, id),
	}))
	options = !multipleChoice
		? [{ id: ALL, label: 'product.filters.allItems.radiobutton' }, ...options]
		: options
	return {
		label,
		options,
		type,
		id,
		multipleChoice,
		selection: multipleChoice ? [] : [ALL],
	}
}

function colorGroupsMapper(filter: ColorsFilter): UserFilter {
	const { type } = filter
	const selection: string[] = []
	const options = [...filter.colorGroups].map((choice) => ({
		...choice,
		id: getOptionId(type, choice.id),
	}))
	return {
		selection,
		options,
		type,
		label: 'product.filters.colors.title',
		multipleChoice: true,
	}
}

function sizesMapper(filter: SizesFilter): UserFilter {
	const { type } = filter
	const selection: string[] = []
	const options = filter.sizes.map((item) => ({
		id: getOptionId(type, item),
		label: item,
	}))
	return {
		selection,
		options,
		type,
		label: 'product.filters.sizes.title',
		multipleChoice: true,
	}
}

function priceMapper(filter: PriceFilter): UserFilter | undefined {
	const { type, onSale } = filter
	if (!onSale) {
		return {
			type,
			label: 'product.filters.prices.title',
			multipleChoice: true,
			selection: [],
			options: [],
		}
	}
	const selection: string[] = []
	const options = [
		{
			id: getOptionId(type, ON_SALE),
			label: 'product.filters.prices.sales.checkbox',
		},
		{
			id: getOptionId(type, NEW_COLLECTION),
			label: 'product.filters.prices.new.checkbox',
		},
	]
	return {
		selection,
		options,
		type,
		label: 'product.filters.prices.title',
		multipleChoice: true,
	}
}

function orderMapper(filter: OrderFilter): UserFilter | undefined {
	const { type } = filter
	const selection: string[] = []
	const options = [
		{
			id: getOptionId(type, 'asc'),
			label: 'product.filters.orderBy.asc',
		},
		{
			id: getOptionId(type, 'desc'),
			label: 'product.filters.orderBy.desc',
		},
	]
	return {
		selection,
		options,
		type,
		label: 'product.filters.orderBy.title',
		multipleChoice: false,
	}
}

const filterOptionsMappers = {
	generic: (filter: Filter) => genericMapper(filter as GenericFilter),
	colorGroups: (filter: Filter) => colorGroupsMapper(filter as ColorsFilter),
	sizes: (filter: Filter) => sizesMapper(filter as SizesFilter),
	price: (filter: Filter) => priceMapper(filter as PriceFilter),
	order: (filter: Filter) => orderMapper(filter as OrderFilter),
}

export function mappers(filtersData: FiltersData): UserFilter[] {
	const filters: UserFilter[] = filtersData.filters
		.map((filter) => {
			const mapper = filterOptionsMappers[filter.type]
			const mapped = mapper(filter) as UserFilter | GenericUserFilter
			const { selection, options, type, label, multipleChoice } = mapped
			return {
				label,
				type,
				multipleChoice,
				selection,
				options,
				id: (mapped as GenericUserFilter).id,
			}
		})
		.flat()
	return filters
}
